import React, { useCallback, useMemo } from 'react';
import { Download } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface DocumentViewerProps {
  objectUrl: string;
  contentType?: string | null;
  filename?: string | null;
  height?: string;
  fullscreen?: boolean;
}

const VIEWABLE_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/svg+xml',
  'video/mp4',
  'video/webm',
  'video/ogg',
  'audio/mpeg',
  'audio/wav',
  'audio/ogg',
];

export const DocumentViewer: React.FC<DocumentViewerProps> = ({
  objectUrl,
  contentType,
  filename,
  height = '700px',
  fullscreen = false,
}) => {
  const isViewable = useMemo(
    () =>
      contentType
        ? VIEWABLE_TYPES.some((type) =>
            contentType.toLowerCase().includes(type.toLowerCase()),
          )
        : false,
    [contentType],
  );

  const mediaType = contentType?.split('/')[0];
  const previewHeight = fullscreen ? '100vh' : height;

  const handleDownload = useCallback(() => {
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename || 'document';
    link.click();
  }, [objectUrl, filename]);

  const renderDownloadButton = () => (
    <Button
      size="sm"
      variant="outline"
      onClick={handleDownload}
      className="gap-2"
    >
      <Download className="h-4 w-4" />
      Download
    </Button>
  );

  if (!isViewable) {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between rounded-lg border border-dashed border-gray-400 bg-gray-50 px-4 py-8 gap-4">
          <div className="text-sm text-gray-500">
            Arquivo não suporta previsualização
          </div>
          {renderDownloadButton()}
        </div>
      </div>
    );
  }

  return (
    <div className={fullscreen ? 'h-screen w-screen' : 'flex flex-col gap-4'}>
      {mediaType === 'image' && (
        <div
          className={
            fullscreen
              ? 'flex h-screen w-screen items-center justify-center bg-neutral-100'
              : 'flex h-full items-center justify-center rounded-lg bg-neutral-100'
          }
        >
          <img
            src={objectUrl}
            alt="Document"
            className={
              fullscreen
                ? 'max-h-screen max-w-screen object-contain'
                : 'max-h-full max-w-full object-contain'
            }
          />
        </div>
      )}

      {mediaType === 'video' && (
        <div
          className={
            fullscreen
              ? 'flex h-screen w-screen items-center justify-center bg-neutral-900'
              : 'flex h-full items-center justify-center rounded-lg bg-neutral-900'
          }
        >
          <video
            controls
            className="max-h-full max-w-full"
            src={objectUrl}
            style={{ maxHeight: previewHeight }}
          />
        </div>
      )}

      {mediaType === 'audio' && (
        <div className="flex items-center justify-center rounded-lg bg-neutral-50 py-8">
          <audio controls className="w-full max-w-md" src={objectUrl} />
        </div>
      )}

      {contentType === 'application/pdf' && (
        <embed
          src={objectUrl}
          type="application/pdf"
          width={fullscreen ? '100vw' : '100%'}
          height={previewHeight}
          className={fullscreen ? 'h-screen w-screen' : 'rounded-lg'}
        />
      )}
    </div>
  );
};
